/**
 * The sign-in session epoch (#967). It moves when the signed-in user goes
 * away (sign-out, or an expired session: `noteSessionOwner(null)`) and when a
 * different user signs in. A ticket operation captures it when it starts;
 * its answer may write the ticket into the query cache (`setTicketDetail`)
 * or any other shared state only while the epoch still matches, so one
 * user's late answer never lands in the next user's session.
 */
let epoch = 0;
let owner = null;
const listeners = new Set();
export function getSessionEpoch() {
    return epoch;
}
export function isCurrentSessionEpoch(captured) {
    return captured === epoch;
}
/** The signed-in user changed: `next` is their id, or null when signed out. */
export function noteSessionOwner(next) {
    if (next === owner)
        return;
    const moved = owner !== null;
    if (moved)
        epoch += 1;
    owner = next;
    if (!moved)
        return;
    for (const listener of listeners) {
        // A failing listener must not stop the others or the sign-out itself.
        try {
            listener();
        }
        catch {
            /* ignore */
        }
    }
}
/**
 * Runs `listener` each time the epoch moves (sign-out, expiry, user switch),
 * after it moved. Used to drop one person's cached data before the next
 * person's session renders. Returns the unsubscribe.
 */
export function onSessionEpochChange(listener) {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}
/** Thrown in place of an answer that arrived after the session changed. */
export class StaleSessionError extends Error {
    code = 'stale_session';
    constructor() {
        super('The signed-in session changed; this ticket operation was dropped');
        this.name = 'StaleSessionError';
    }
}
export function isStaleSession(err) {
    return err?.code === 'stale_session';
}
export function __resetSessionEpochForTest() {
    epoch = 0;
    owner = null;
}
//# sourceMappingURL=sessionEpoch.js.map